import { useTaskWithErrorHandling } from "@/hooks/useTaskWithErrorHandling";
import { findContractBySearch } from "@/Api/PublicRequestApi";
import { usePublicRequestProvider } from "@/Provider/PublicRequestProvider";
import { FieldValues } from "react-hook-form";
import {
    magicLinkPageColumnSx,
    publicRequestContentSx,
} from "@/Utils/MagicLink/pageLayout";
import { LoadingButton } from "@mui/lab";
import { Box, CircularProgress, Typography, useTheme } from "@mui/material";
import { MessageAlert } from "@/Components/MessageAlert";
import { ArrowRightIcon } from "request-form-lib";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { buildPublicTokenPath } from "@/Router/publicRoutes";
import { SxProps, Theme } from "@mui/material/styles";
import { RequestMasterTypeEnum } from "@/Schemas/PublicRequest/RequestMasterTypeEnum";

const MOBILE_CTA_FOOTER_HEIGHT_PX = 88;

const searchViewContentSx: SxProps<Theme> = {
    ...publicRequestContentSx,
    paddingX: { xs: "16px", md: 2 },
    paddingY: { xs: "24px", md: "80px" },
    maxWidth: "100%",
    minWidth: 0,
    overflowX: "hidden",
};

type SearchViewProps = {
    requestMasterType: RequestMasterTypeEnum;
};

const SearchView = ({
    requestMasterType,
}: SearchViewProps) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const {
        findContractForm,
        getRecaptchaCode,
        setContractData,
        setFindContractInput,
        masterTypeConfig,
        setRequestMasterType,
        setSearchRequestMasterType,
    } = usePublicRequestProvider();

    useEffect(() => {
        setRequestMasterType(requestMasterType);
    }, [requestMasterType]);

    const FormComponent = masterTypeConfig?.formComponent;

    const [hasSearchError, setHasSearchError] = useState(false);

    const [contractErrorOccured, setContractErrorOccured] = useState(() => false);
    const [genericErrorMessage, setGenericErrorMessage] = useState(() => "");

    const notFoundMessage = masterTypeConfig?.invalidContractOnSearchView;

    const searchErrorAlertText = contractErrorOccured && notFoundMessage ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography variant="subtitle1" component="div" fontWeight={600}>
                {notFoundMessage.headline}
            </Typography>
            <Typography variant="body2" component="div">
                {notFoundMessage.description}
            </Typography>
        </Box>
    ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography variant="subtitle1" component="div" fontWeight={600}>
                {genericErrorMessage}
            </Typography>
        </Box>
    );

    const {
        handleSubmit,
        formState: { isValid },
    } = findContractForm;

    const validateSearchTask = useTaskWithErrorHandling({
        asyncFunction: async (fields: FieldValues) => {
            setHasSearchError(false);
            const recaptchaResponse = await getRecaptchaCode();
            const data = await findContractBySearch({
                requestMasterTypeId: requestMasterType!.valueOf(),
                fields,
                recaptcha: recaptchaResponse
            });

            setContractData(data);
            setFindContractInput(fields);
            setSearchRequestMasterType(requestMasterType!);

            navigate(buildPublicTokenPath(data.token), { replace: true });
        },
        onError: (message) => {
            setContractData(null);
            setContractErrorOccured(message === "PUBLIC_REQUEST_INVALID")
            setGenericErrorMessage(message !== "PUBLIC_REQUEST_INVALID"
                ? message
                : "Zmluva sa nenašla alebo požadovaný úkon nie je dostupný. Skúste to prosím neskôr."
            )
            setHasSearchError(true);
        },
        genericErrorMessage: "Nastala chyba počas spracovania požiadavky. Skúste to prosím neskôr.",
    });

    const onSubmit = (data: FieldValues) => {
        validateSearchTask.perform(data);
    };

    const isSearching = validateSearchTask.isRunning;
    const isContinueDisabled = !isValid;
    const continueButtonIconColor = isContinueDisabled
        ? "#666666"
        : theme.palette.primary.contrastText;

    return (
        <Box component="main" sx={searchViewContentSx}>
            <Box
                sx={{
                    ...magicLinkPageColumnSx,
                    maxWidth: { xs: "100%", md: "682px" },
                    minWidth: { xs: 0, md: 682 },
                    width: "100%",
                }}
            >
                <Box
                    component="form"
                    onSubmit={handleSubmit(onSubmit)}
                    noValidate
                    sx={{
                        width: "100%",
                        minWidth: 0,
                        display: "flex",
                        flexDirection: "column",
                        gap: "32px",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: { xs: "12px", md: "32px" },
                            width: "100%",
                        }}
                    >
                        {hasSearchError && searchErrorAlertText && (
                            <Box sx={{ display: { xs: "block", md: "none" }, width: "100%" }}>
                                <MessageAlert
                                    severity="error"
                                    text={searchErrorAlertText}
                                    sx={{ marginTop: 0 }}
                                />
                            </Box>
                        )}

                        {FormComponent && <FormComponent />}

                        {hasSearchError && searchErrorAlertText && (
                            <Box sx={{ display: { xs: "none", md: "block" }, width: "100%" }}>
                                <MessageAlert
                                    severity="error"
                                    text={searchErrorAlertText}
                                    sx={{ marginTop: 0 }}
                                />
                            </Box>
                        )}
                    </Box>

                    <Box
                        aria-hidden
                        sx={{
                            display: { xs: "block", md: "none" },
                            flexShrink: 0,
                            height: `${MOBILE_CTA_FOOTER_HEIGHT_PX}px`,
                        }}
                    />

                    <Box
                        sx={(theme) => ({
                            position: { xs: "fixed", md: "static" },
                            bottom: { xs: 0, md: "auto" },
                            left: { xs: 0, md: "auto" },
                            right: { xs: 0, md: "auto" },
                            zIndex: { xs: 1100, md: "auto" },
                            backgroundColor: {
                                xs: theme.palette.background.default,
                                md: "transparent",
                            },
                            borderTop: { xs: `1px solid ${theme.palette.divider}`, md: "none" },
                            paddingX: { xs: "16px", md: 0 },
                            paddingTop: { xs: 2, md: 0 },
                            paddingBottom: {
                                xs: "max(16px, env(safe-area-inset-bottom))",
                                md: 0,
                            },
                            display: "flex",
                            justifyContent: { xs: "stretch", md: "flex-end" },
                            width: "100%",
                        })}
                    >
                        <LoadingButton
                            type="submit"
                            variant="contained"
                            color="primary"
                            loading={isSearching}
                            loadingPosition="center"
                            loadingIndicator={<CircularProgress size={22} color="inherit" />}
                            disabled={isContinueDisabled}
                            endIcon={
                                !isSearching ? (
                                    <ArrowRightIcon
                                        width={18}
                                        height={18}
                                        color={continueButtonIconColor}
                                    />
                                ) : undefined
                            }
                            sx={{
                                width: { xs: "100%", md: "fit-content" },
                                minWidth: { md: isSearching ? 140 : undefined },
                                height: 48,
                                minHeight: 48,
                                textTransform: "none",
                                "&.Mui-disabled": {
                                    backgroundColor: "#E5E5E5",
                                    color: "#666666",
                                },
                            }}
                        >
                            {!isSearching && "Pokračovať"}
                        </LoadingButton>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default SearchView;
